import {
  ApiClientError,
  type AccessRequestResponse,
  type AuthBootstrapResponse,
  type MeResponse,
  type MembershipSummaryResponse,
  type MyAccessRequestStatusResponse,
  type Permission,
  type PendingInvitationResponse,
  type PendingPlatformInvitationResponse,
  type RegisteredSessionBootstrapResponse,
  type SessionRenewal,
  type SessionUserResponse,
  type SubmitJoinAccessRequest,
  type SubmitNewTenantAccessRequest,
} from '@contract-analyzer/api-client';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import {
  AuthContext,
  type AuthUser,
  type OnboardingIdentity,
} from '@/hooks/auth-context';
import { apiClient } from '@/lib/api';
import {
  publishAuthFailed,
  publishAuthRenewed,
  publishAuthSignedOut,
  subscribeAuthRecovery,
  subscribeAuthRenewed,
  subscribeAuthSignedOut,
  subscribeSessionDeadline,
} from '@/lib/auth-events';
import { approveSessionChange, SessionChangeDeclinedError } from '@/lib/session-change-guard';
import {
  SESSION_ENDED,
  SessionLifecycle,
  type SessionGeneration,
  unlessEnded,
} from '@/lib/session-lifecycle';
import { acceptedCurrentTerms, CURRENT_TERMS_VERSION } from '@/lib/terms';
import {
  IdentityTokenAcquisitionError,
  type DevPersona,
  type IdentityTokenResult,
  acquireIdentityTokenSilent,
  createDevIdentityProviderClient,
  isDevAuthMode,
  signInInteractively,
  startDevSessionBootstrap,
  startInteractiveSessionBootstrap,
} from '@/lib/session-auth';
import {
  SessionExpiredDialog,
  type SessionRenewalDialogState,
} from '@/components/layout/session-expired-dialog';
import { withCleanup } from '@/lib/with-cleanup';

const HAS_VISITED_STORAGE_KEY = 'contract-analyzer:has-visited';
const ONBOARDING_STORAGE_KEY = 'contract-analyzer:onboarding';
// Window after a successful renewal during which we ignore further auth-required
// signals. Stale in-flight 401s often arrive moments after recovery completes;
// re-prompting for them produces a "Continue session" flash loop.
const SESSION_RENEWAL_COOLDOWN_MS = 30 * 1000;
// After an interactive bootstrap the browser normally has the freshly-issued
// session cookie stored by the time the response resolves, but a small number of
// MSAL popup + third-party-cookie configurations have been observed to miss the
// cookie on the very next fetch in the same tab. Retrying the cookie-backed
// /users/me request once after a short delay absorbs that race so the page
// rehydrates instead of forcing the "reopen in a new tab" workaround.
const POST_BOOTSTRAP_COOKIE_RETRY_DELAY_MS = 150;

async function fetchCurrentUserWithCookieRetry(): Promise<MeResponse> {
  try {
    return await apiClient.getCurrentUser();
  } catch (error) {
    if (error instanceof ApiClientError && error.status === 401) {
      await new Promise<void>((resolve) => {
        setTimeout(resolve, POST_BOOTSTRAP_COOKIE_RETRY_DELAY_MS);
      });
      return await apiClient.getCurrentUser();
    }
    throw error;
  }
}
/** Whether the session whose deadline is `deadline` (Unix seconds) has run out by `nowMs`. */
function sessionHasRunOut(deadline: number | null, nowMs: number): boolean {
  return deadline !== null && deadline * 1000 <= nowMs;
}

type StoredOnboardingState = {
  readonly identity: OnboardingIdentity;
  readonly request: AccessRequestResponse | null;
  readonly token: string;
};

function buildAuthUser(sessionUser: SessionUserResponse, currentUser: MeResponse): AuthUser {
  return {
    accepted_terms_at: currentUser.accepted_terms_at ?? null,
    created_at: currentUser.created_at,
    demo_mode: sessionUser.demo_mode,
    display_name: currentUser.display_name,
    email: currentUser.email,
    is_superadmin: sessionUser.is_superadmin,
    membership_id: sessionUser.membership_id,
    permissions: sessionUser.tenant_permissions,
    status: currentUser.status,
    tenant_display_name: sessionUser.tenant_display_name,
    tenant_id: sessionUser.tenant_id,
    tenant_slug: sessionUser.tenant_slug,
    user_audience: currentUser.user_audience,
    user_id: currentUser.user_id,
  };
}

/**
 * Renewing keeps the workspace the page is working in. By the time a session is renewed it
 * has usually ended, so the server can no longer read that workspace from it (#1406).
 */
function renewalOf(user: AuthUser | null): SessionRenewal {
  return { renewedTenantId: user?.tenant_id ?? null };
}

/** How long a silent renewal waits for the identity provider before asking the user instead. */
const SILENT_TOKEN_TIMEOUT_MS = 20_000;

/**
 * How a silent renewal ended. Only a failed one asks the user to continue; an ended one
 * belongs to a session the user signed out of, here or in another tab.
 */
type RenewalOutcome = 'renewed' | 'failed' | 'ended';

/**
 * A silent identity token, or null when the provider has none or doesn't answer in time. A
 * stalled provider must not hold the workspace switch queued behind the renewal.
 */
async function acquireSilentTokenInTime(): Promise<IdentityTokenResult | null> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const late = new Promise<null>((resolve) => {
    timer = setTimeout(() => { resolve(null); }, SILENT_TOKEN_TIMEOUT_MS);
  });
  try {
    return await Promise.race([acquireIdentityTokenSilent(), late]);
  } finally {
    clearTimeout(timer);
  }
}

function hasAcceptedCurrentTerms(user: AuthUser | null): boolean {
  return user !== null && acceptedCurrentTerms(user.accepted_terms_at);
}

function markUserAsVisited(): void {
  localStorage.setItem(HAS_VISITED_STORAGE_KEY, '1');
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function readOnboardingIdentity(value: unknown): OnboardingIdentity | null {
  if (!isRecord(value)) {
    return null;
  }

  const providerSubject =
    typeof value['provider_subject'] === 'string'
      ? value['provider_subject']
      : typeof value['user_id'] === 'string'
        ? value['user_id']
        : null;
  if (
    typeof value['display_name'] !== 'string' ||
    typeof value['email'] !== 'string' ||
    typeof value['identity_provider_tenant_id'] !== 'string' ||
    providerSubject === null
  ) {
    return null;
  }

  return {
    display_name: value['display_name'],
    email: value['email'],
    identity_provider_tenant_id: value['identity_provider_tenant_id'],
    provider_subject: providerSubject,
  };
}

function isAccessRequestResponse(value: unknown): value is AccessRequestResponse {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof value['created_at'] === 'string' &&
    typeof value['request_id'] === 'string' &&
    typeof value['requester_display_name'] === 'string' &&
    typeof value['requester_email'] === 'string' &&
    (value['request_type'] === 'new_tenant' || value['request_type'] === 'join_tenant') &&
    (value['status'] === 'pending' || value['status'] === 'approved' || value['status'] === 'rejected')
  );
}

function isStoredOnboardingState(value: unknown): value is StoredOnboardingState {
  if (
    !isRecord(value) ||
    typeof value['token'] !== 'string' ||
    readOnboardingIdentity(value['identity']) === null
  ) {
    return false;
  }

  return value['request'] === null || isAccessRequestResponse(value['request']);
}

function readStoredOnboardingState(): StoredOnboardingState | null {
  const rawState = sessionStorage.getItem(ONBOARDING_STORAGE_KEY);
  if (rawState === null || rawState.trim().length === 0) {
    return null;
  }

  let parsedState: unknown;
  try {
    parsedState = JSON.parse(rawState);
  } catch (error) {
    if (error instanceof SyntaxError) {
      sessionStorage.removeItem(ONBOARDING_STORAGE_KEY);
      return null;
    }

    throw error;
  }

  if (!isStoredOnboardingState(parsedState)) {
    sessionStorage.removeItem(ONBOARDING_STORAGE_KEY);
    return null;
  }

  const identity = readOnboardingIdentity(parsedState['identity']);
  if (identity === null) {
    sessionStorage.removeItem(ONBOARDING_STORAGE_KEY);
    return null;
  }

  return {
    identity,
    request: parsedState['request'],
    token: parsedState['token'],
  };
}

function writeStoredOnboardingState(state: StoredOnboardingState | null): void {
  if (state === null) {
    sessionStorage.removeItem(ONBOARDING_STORAGE_KEY);
    return;
  }

  sessionStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(state));
}

function toAuthErrorMessage(error: unknown, fallbackMessage: string): string {
  if (error instanceof ApiClientError) {
    return error.message;
  }

  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  return fallbackMessage;
}

export function AuthProvider({ children }: { readonly children: ReactNode }) {
  const [availableMemberships, setAvailableMemberships] = useState<
    readonly MembershipSummaryResponse[]
  >([]);
  const [error, setError] = useState<string | null>(null);
  const [hasAuthenticatedSession, setHasAuthenticatedSession] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [onboardingIdentity, setOnboardingIdentity] = useState<OnboardingIdentity | null>(null);
  const [onboardingRequest, setOnboardingRequest] = useState<AccessRequestResponse | null>(null);
  const [pendingInvitations, setPendingInvitations] = useState<
    readonly PendingInvitationResponse[]
  >([]);
  const [pendingPlatformInvitations, setPendingPlatformInvitations] = useState<
    readonly PendingPlatformInvitationResponse[]
  >([]);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [showExpiredDialog, setShowExpiredDialog] = useState(false);
  const [sessionRenewalState, setSessionRenewalState] =
    useState<SessionRenewalDialogState>('action_required');
  const [sessionRenewalError, setSessionRenewalError] = useState<string | null>(null);
  // When the session runs out (Unix seconds), kept beside the user rather than in it. User
  // activity moves it every minute or so, and a page keyed on the user would reload whatever
  // it shows each time it moved. The state schedules the expiry prompt; the ref is the same
  // value for handlers, written with it.
  const [sessionDeadline, setSessionDeadline] = useState<number | null>(null);
  const sessionDeadlineRef = useRef<number | null>(null);
  const renewalPromiseRef = useRef<Promise<RenewalOutcome> | null>(null);
  // A sign-out ends the session generation, and whatever began before it may no longer
  // adopt a session (#1175); see `SessionLifecycle`.
  const sessionLifecycleRef = useRef<SessionLifecycle | null>(null);
  const sessionOperationRef = useRef<Promise<unknown>>(Promise.resolve());
  const userRef = useRef<AuthUser | null>(null);
  const showExpiredDialogRef = useRef(false);
  const lastSuccessfulRenewalAtRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    showExpiredDialogRef.current = showExpiredDialog;
  }, [showExpiredDialog]);

  const recordSessionDeadline = useCallback((deadline: number | null) => {
    sessionDeadlineRef.current = deadline;
    setSessionDeadline(deadline);
  }, []);

  useEffect(() => subscribeSessionDeadline((expiresAt) => {
    // Responses can land out of order, and a slow non-extending poll carries an
    // older deadline than a user request that has since slid the session. The
    // deadline only moves forward within one session, so take the later value.
    const knownDeadline = sessionDeadlineRef.current;
    if (knownDeadline !== null && expiresAt <= knownDeadline) {
      return;
    }
    recordSessionDeadline(expiresAt);
  }), [recordSessionDeadline]);

  const sessionLifecycle = useCallback((): SessionLifecycle => {
    if (sessionLifecycleRef.current === null) sessionLifecycleRef.current = new SessionLifecycle();
    return sessionLifecycleRef.current;
  }, []);

  /** A sign-out, here or in another tab: nothing that began before it adopts a session. */
  const endSessionGeneration = useCallback(() => {
    sessionLifecycle().end();
    renewalPromiseRef.current = null;
  }, [sessionLifecycle]);

  const runSerializedSessionOperation = useCallback(
    <T,>(operation: () => Promise<T>): Promise<T> => {
      const previousOperation = sessionOperationRef.current.catch(() => undefined);
      const nextOperation = previousOperation.then(operation);
      sessionOperationRef.current = nextOperation.catch(() => undefined);
      return nextOperation;
    },
    [],
  );

  const showSessionContinuation = useCallback((
    state: SessionRenewalDialogState,
    errorMessage: string | null = null,
  ) => {
    setSessionRenewalState(state);
    setSessionRenewalError(errorMessage);
    setShowExpiredDialog(true);
  }, []);

  const clearOnboardingState = useCallback(() => {
    writeStoredOnboardingState(null);
    setOnboardingIdentity(null);
    setOnboardingRequest(null);
  }, []);

  const clearAuthenticatedSessionState = useCallback(() => {
    recordSessionDeadline(null);
    setHasAuthenticatedSession(false);
    setAvailableMemberships([]);
    setPendingInvitations([]);
    setPendingPlatformInvitations([]);
    setUser(null);
  }, [recordSessionDeadline]);

  /**
   * Adopts `session` for work that captured `generation` when it began; false, changing
   * nothing, when a sign-out came first.
   */
  const applyAuthenticatedSession = useCallback(async (
    session: Awaited<ReturnType<typeof apiClient.getAuthSession>>,
    generation: SessionGeneration,
  ): Promise<boolean> => {
    if (!sessionLifecycle().isCurrent(generation)) return false;
    // Memberships land before the user is read, as they always have; the shell starts
    // resolving the workspace from them.
    setAvailableMemberships(session.available_memberships);
    setPendingInvitations(session.pending_invitations);
    setPendingPlatformInvitations(session.pending_platform_invitations);

    if (!session.authenticated || session.user == null) {
      setHasAuthenticatedSession(false);
      setUser(null);
      return false;
    }

    const currentUser = await apiClient.getCurrentUser();
    if (!sessionLifecycle().adopt(generation)) return false;
    // A session payload describes the session it was issued for, so its deadline replaces
    // any carried over from the session it supersedes: a tenant switch inherits the
    // absolute cap and can legitimately land earlier.
    recordSessionDeadline(session.user.expires_at);
    setUser(buildAuthUser(session.user, currentUser));
    setHasAuthenticatedSession(true);
    return true;
  }, [recordSessionDeadline, sessionLifecycle]);

  const storeOnboardingState = useCallback(
    (state: StoredOnboardingState) => {
      writeStoredOnboardingState(state);
      clearAuthenticatedSessionState();
      setOnboardingIdentity(state.identity);
      setOnboardingRequest(state.request);
    },
    [clearAuthenticatedSessionState],
  );

  const hydrateAuthenticatedUser = useCallback(
    async (
      bootstrapSession: {
        readonly user: SessionUserResponse;
        readonly availableMemberships: readonly MembershipSummaryResponse[];
        readonly pendingInvitations: readonly PendingInvitationResponse[];
        readonly pendingPlatformInvitations: readonly PendingPlatformInvitationResponse[];
      } | undefined,
      generation: SessionGeneration,
    ): Promise<boolean> => {
      // When the caller has just completed an interactive bootstrap, use the
      // registered session payload directly instead of issuing a second cookie-
      // backed /auth/session request. That follow-up call is what races with
      // the browser's cookie-persistence step on the same tab and produces the
      // stuck "Continue session" state that the "reopen in a new tab"
      // workaround was papering over.
      let sessionUser: SessionUserResponse;
      let availableMemberships: readonly MembershipSummaryResponse[];
      let invitations: readonly PendingInvitationResponse[];
      let platformInvitations: readonly PendingPlatformInvitationResponse[];
      if (bootstrapSession !== undefined) {
        sessionUser = bootstrapSession.user;
        availableMemberships = bootstrapSession.availableMemberships;
        invitations = bootstrapSession.pendingInvitations;
        platformInvitations = bootstrapSession.pendingPlatformInvitations;
      } else {
        const session = await apiClient.getAuthSession();
        if (!session.authenticated || session.user == null) {
          throw new Error(
            'The secure sign-in completed, but the browser session cookie was not established. Check cookie settings and try again.',
          );
        }
        sessionUser = session.user;
        availableMemberships = session.available_memberships;
        invitations = session.pending_invitations;
        platformInvitations = session.pending_platform_invitations;
      }

      const currentUser = await fetchCurrentUserWithCookieRetry();
      if (!sessionLifecycle().adopt(generation)) return false;
      clearOnboardingState();
      setHasAuthenticatedSession(true);
      recordSessionDeadline(sessionUser.expires_at);
      setUser(buildAuthUser(sessionUser, currentUser));
      setAvailableMemberships(availableMemberships);
      setPendingInvitations(invitations);
      setPendingPlatformInvitations(platformInvitations);
      markUserAsVisited();
      return true;
    },
    [clearOnboardingState, recordSessionDeadline, sessionLifecycle],
  );

  const reconcileAuthenticatedSession = useCallback(async (
    generation: SessionGeneration,
  ): Promise<boolean> => {
    const session = await apiClient.getAuthSession();
    if (!sessionLifecycle().isCurrent(generation)) return false;
    if (!session.authenticated) {
      clearAuthenticatedSessionState();
      return false;
    }

    clearOnboardingState();
    if (!(await applyAuthenticatedSession(session, generation))) return false;
    markUserAsVisited();
    setError(null);
    setShowExpiredDialog(false);
    return true;
  }, [applyAuthenticatedSession, clearAuthenticatedSessionState, clearOnboardingState, sessionLifecycle]);

  /** Applies a bootstrap's result; false, changing nothing, when a sign-out came first. */
  const applyBootstrapResult = useCallback(
    async (
      bootstrap: AuthBootstrapResponse,
      token: string,
      generation: SessionGeneration,
    ): Promise<boolean> => {
      if (!sessionLifecycle().isCurrent(generation)) return false;
      if (bootstrap.status === 'registered') {
        return await hydrateAuthenticatedUser({
          user: bootstrap.user,
          availableMemberships: bootstrap.available_memberships,
          pendingInvitations: bootstrap.pending_invitations,
          pendingPlatformInvitations: bootstrap.pending_platform_invitations,
        }, generation);
      }

      if (bootstrap.status === 'pending_request') {
        storeOnboardingState({
          identity: bootstrap.identity,
          request: bootstrap.request,
          token,
        });
        markUserAsVisited();
        return true;
      }

      storeOnboardingState({
        identity: bootstrap.identity,
        request: null,
        token,
      });
      markUserAsVisited();
      return true;
    },
    [hydrateAuthenticatedUser, sessionLifecycle, storeOnboardingState],
  );

  /**
   * A token for an onboarding step that captured `generation`. A failure that lands after a
   * sign-out ended that generation asks nothing of the user: there is no session to continue.
   */
  const acquireFreshOnboardingToken = useCallback(async (
    fallbackMessage: string,
    generation: SessionGeneration,
  ): Promise<string> => {
    try {
      const tokenResult = await acquireIdentityTokenSilent();
      if (tokenResult !== null) {
        return tokenResult.token;
      }
    } catch (error) {
      const message = toAuthErrorMessage(error, fallbackMessage);
      if (sessionLifecycle().isCurrent(generation)) {
        setError(message);
        showSessionContinuation('action_required');
      }
      throw new Error(message, { cause: error });
    }

    const storedOnboardingState = readStoredOnboardingState();
    const storedToken = storedOnboardingState?.token.trim();
    if (
      storedOnboardingState !== null &&
      onboardingIdentity !== null &&
      storedOnboardingState.identity.provider_subject === onboardingIdentity.provider_subject &&
      storedOnboardingState.identity.identity_provider_tenant_id === onboardingIdentity.identity_provider_tenant_id &&
      storedToken !== undefined &&
      storedToken.length > 0
    ) {
      return storedToken;
    }

    if (sessionLifecycle().isCurrent(generation)) {
      setError(fallbackMessage);
      showSessionContinuation('action_required');
    }
    throw new Error(fallbackMessage);
  }, [onboardingIdentity, sessionLifecycle, showSessionContinuation]);

  // Both renewals, the silent one and the one the user confirms, go through these two steps;
  // only where their identity token comes from differs.
  const bootstrapRenewal = useCallback(async (
    token: string,
    generation: SessionGeneration,
  ): Promise<AuthBootstrapResponse | typeof SESSION_ENDED> => {
    if (!sessionLifecycle().isCurrent(generation)) return SESSION_ENDED;
    // A sign-out aborts the request with its generation, so the browser drops the session the
    // server may create for it unless that response had already arrived. Nothing is signed
    // out by cookie: by then the cookie can belong to a sign-in that came after.
    const bootstrap = await apiClient.bootstrapIdentityFromToken(
      token,
      renewalOf(userRef.current),
      { signal: generation.signal },
    );
    return sessionLifecycle().isCurrent(generation) ? bootstrap : SESSION_ENDED;
  }, [sessionLifecycle]);

  /** Adopts a renewed session; false when a sign-out ended its generation first. */
  const adoptRenewedSession = useCallback(async (
    bootstrap: RegisteredSessionBootstrapResponse,
    generation: SessionGeneration,
  ): Promise<boolean> => {
    const adopted = await hydrateAuthenticatedUser({
      user: bootstrap.user,
      availableMemberships: bootstrap.available_memberships,
      pendingInvitations: bootstrap.pending_invitations,
      pendingPlatformInvitations: bootstrap.pending_platform_invitations,
    }, generation);
    if (!adopted) return false;
    setError(null);
    setSessionRenewalError(null);
    setSessionRenewalState('action_required');
    setShowExpiredDialog(false);
    lastSuccessfulRenewalAtRef.current = Date.now();
    publishAuthRenewed();
    return true;
  }, [hydrateAuthenticatedUser]);

  const renewSessionSilently = useCallback(async (): Promise<RenewalOutcome> => {
    if (renewalPromiseRef.current !== null) {
      return await renewalPromiseRef.current;
    }

    const lifecycle = sessionLifecycle();
    if (!lifecycle.admitsRenewal()) return 'ended';
    // Taken when the renewal is asked for: a sign-out while it waits its turn ends it too.
    const generation = lifecycle.capture();
    const renewalPromise = runSerializedSessionOperation(async (): Promise<RenewalOutcome> => {
      if (!lifecycle.isCurrent(generation)) return 'ended';
      setSessionRenewalState('checking');
      setSessionRenewalError(null);
      try {
        const tokenResult = await unlessEnded(generation, acquireSilentTokenInTime());
        if (tokenResult === SESSION_ENDED) return 'ended';
        if (tokenResult === null) {
          setSessionRenewalState('action_required');
          publishAuthFailed();
          return 'failed';
        }

        const bootstrap = await bootstrapRenewal(tokenResult.token, generation);
        if (bootstrap === SESSION_ENDED) return 'ended';
        if (bootstrap.status !== 'registered') {
          setSessionRenewalState('action_required');
          publishAuthFailed();
          return 'failed';
        }

        return await adoptRenewedSession(bootstrap, generation) ? 'renewed' : 'ended';
      } catch {
        if (!lifecycle.isCurrent(generation)) return 'ended';
        setSessionRenewalState('action_required');
        publishAuthFailed();
        return 'failed';
      }
    }).finally(() => {
      if (renewalPromiseRef.current === renewalPromise) {
        renewalPromiseRef.current = null;
      }
    });

    renewalPromiseRef.current = renewalPromise;
    return await renewalPromise;
  }, [adoptRenewedSession, bootstrapRenewal, runSerializedSessionOperation, sessionLifecycle]);

  useEffect(() => subscribeAuthRecovery(() => {
    // Signing out keeps the user on screen until the server answers; a 401 meanwhile has no
    // session left to recover.
    if (userRef.current === null || !sessionLifecycle().admitsRenewal()) {
      return false;
    }

    if (sessionHasRunOut(sessionDeadlineRef.current, Date.now())) {
      showSessionContinuation('action_required');
      return false;
    }

    // Coalesce repeat triggers: if the renewal dialog is already presented,
    // or we just successfully renewed (in-flight 401s often arrive after
    // recovery completes), treat this as already handled to break the
    // "Continue session" flash loop.
    if (showExpiredDialogRef.current) {
      return true;
    }
    const lastRenewalAt = lastSuccessfulRenewalAtRef.current;
    if (lastRenewalAt !== null && Date.now() - lastRenewalAt < SESSION_RENEWAL_COOLDOWN_MS) {
      return true;
    }

    return renewSessionSilently().then((outcome) => {
      if (outcome === 'failed') {
        showSessionContinuation('action_required');
      }
      return outcome === 'renewed';
    });
  }), [renewSessionSilently, sessionLifecycle, showSessionContinuation]);

  useEffect(() => subscribeAuthRenewed((event) => {
    if (event.source === 'local') {
      return;
    }

    const generation = sessionLifecycle().capture();
    void runSerializedSessionOperation(async () => {
      try {
        await reconcileAuthenticatedSession(generation);
      } catch (error) {
        setError(toAuthErrorMessage(error, 'The session could not be refreshed from another tab.'));
      }
    });
  }), [reconcileAuthenticatedSession, runSerializedSessionOperation, sessionLifecycle]);

  useEffect(() => subscribeAuthSignedOut((event) => {
    if (event.source === 'local') {
      return;
    }

    endSessionGeneration();
    clearAuthenticatedSessionState();
    clearOnboardingState();
    setShowExpiredDialog(false);
    setSessionRenewalError(null);
    setSessionRenewalState('action_required');
    setIsLoading(false);
  }), [clearAuthenticatedSessionState, clearOnboardingState, endSessionGeneration]);

  const synchronizeOnboardingRequest = useCallback(
    async (
      token: string,
      identity: OnboardingIdentity,
      generation: SessionGeneration,
    ): Promise<MyAccessRequestStatusResponse> => {
      const requestStatus = await apiClient.getMyAccessRequestStatus(token);
      if (requestStatus.status === 'pending') {
        if (!sessionLifecycle().isCurrent(generation)) return requestStatus;
        storeOnboardingState({
          identity,
          request: requestStatus.request,
          token,
        });
        return requestStatus;
      }

      // An approved applicant is signed in from here, so a sign-out since the click wins and
      // drops the request, as it does a renewal's.
      if (!sessionLifecycle().isCurrent(generation)) return { status: 'none' };
      const bootstrap = await apiClient.bootstrapIdentityFromToken(token, undefined, { signal: generation.signal });
      await applyBootstrapResult(bootstrap, token, generation);
      if (bootstrap.status === 'pending_request') {
        return {
          request: bootstrap.request,
          status: 'pending',
        };
      }

      return { status: 'none' };
    },
    [applyBootstrapResult, sessionLifecycle, storeOnboardingState],
  );

  useEffect(() => {
    const restoreSessionState = { active: true };
    const restoreWasCancelled = (): boolean => !restoreSessionState.active;

    async function restoreSession(): Promise<void> {
      const generation = sessionLifecycle().capture();
      const storedOnboardingState = readStoredOnboardingState();
      let authenticatedSessionDetected = false;
      setError(null);
      setIsLoading(true);
      await withCleanup(async () => {
        try {
          const session = await apiClient.getAuthSession();
          if (session.authenticated) {
            authenticatedSessionDetected = true;
            if (restoreWasCancelled()) {
              return;
            }

            clearOnboardingState();
            await applyAuthenticatedSession(session, generation);
            if (restoreWasCancelled()) {
              return;
            }

            markUserAsVisited();
            return;
          }

          if (restoreWasCancelled()) {
            return;
          }

          if (storedOnboardingState !== null) {
            if (!sessionLifecycle().isCurrent(generation)) return;
            setUser(null);
            setOnboardingIdentity(storedOnboardingState.identity);
            setOnboardingRequest(storedOnboardingState.request);
            return;
          }

          clearOnboardingState();
          clearAuthenticatedSessionState();
        } catch (error) {
          if (restoreWasCancelled()) {
            return;
          }

          if (!authenticatedSessionDetected && storedOnboardingState !== null) {
            if (!sessionLifecycle().isCurrent(generation)) return;
            setUser(null);
            setOnboardingIdentity(storedOnboardingState.identity);
            setOnboardingRequest(storedOnboardingState.request);
            return;
          }

          setError(toAuthErrorMessage(error, 'The session could not be restored. Try signing in again.'));
          clearOnboardingState();
          clearAuthenticatedSessionState();
        }
      }, () => {
        if (!restoreWasCancelled()) {
          setIsLoading(false);
        }
      });
    }

    void restoreSession();

    return () => {
      restoreSessionState.active = false;
    };
  }, [applyAuthenticatedSession, clearAuthenticatedSessionState, clearOnboardingState, sessionLifecycle]);

  const login = useCallback(async (devPersona?: DevPersona): Promise<void> => {
    setError(null);
    setIsLoading(true);
    await withCleanup(async () => {
      try {
        const result = devPersona && isDevAuthMode()
          ? await startDevSessionBootstrap(devPersona)
          : await startInteractiveSessionBootstrap();
        // A sign-in is the user's own request, so a sign-out in another tab while it ran
        // doesn't cancel it: whichever lands last wins. Other tabs follow only what was applied.
        if (await applyBootstrapResult(result.bootstrap, result.token, sessionLifecycle().capture())) {
          publishAuthRenewed();
        }
      } catch (error) {
        const message = toAuthErrorMessage(
          error,
          'The browser session could not be created. Try signing in again.',
        );

        setError(message);
        clearAuthenticatedSessionState();
        throw new Error(message, { cause: error });
      }
    }, () => {
      setIsLoading(false);
    });
  }, [applyBootstrapResult, clearAuthenticatedSessionState, sessionLifecycle]);

  const retryOnboardingBootstrap = useCallback(async (): Promise<void> => {
    if (onboardingIdentity === null) {
      throw new Error('Your onboarding sign-in has expired. Sign in again to continue.');
    }

    const generation = sessionLifecycle().capture();
    setError(null);
    setIsLoading(true);
    await withCleanup(async () => {
      try {
        const token = await acquireFreshOnboardingToken(
          'Confirm your secure sign-in to continue onboarding.',
          generation,
        );
        if (!sessionLifecycle().isCurrent(generation)) return;
        const bootstrap = await apiClient.bootstrapIdentityFromToken(token, undefined, { signal: generation.signal });
        await applyBootstrapResult(bootstrap, token, generation);
      } catch (error) {
        const message = toAuthErrorMessage(
          error,
          'The onboarding session could not be refreshed. Sign in again to continue.',
        );

        if (sessionLifecycle().isCurrent(generation)) setError(message);
        throw new Error(message, { cause: error });
      }
    }, () => {
      setIsLoading(false);
    });
  }, [acquireFreshOnboardingToken, applyBootstrapResult, onboardingIdentity, sessionLifecycle]);

  const refreshOnboardingRequest = useCallback(async (): Promise<MyAccessRequestStatusResponse> => {
    if (onboardingIdentity === null) {
      throw new Error('Your onboarding sign-in has expired. Sign in again to continue.');
    }

    const generation = sessionLifecycle().capture();
    setError(null);
    setIsLoading(true);
    return await withCleanup(async () => {
      try {
        const token = await acquireFreshOnboardingToken(
          'Confirm your secure sign-in to refresh your onboarding request.',
          generation,
        );
        return await synchronizeOnboardingRequest(token, onboardingIdentity, generation);
      } catch (error) {
        const message = toAuthErrorMessage(
          error,
          'The onboarding request status could not be refreshed. Try again.',
        );

        if (sessionLifecycle().isCurrent(generation)) setError(message);
        throw new Error(message, { cause: error });
      }
    }, () => {
      setIsLoading(false);
    });
  }, [acquireFreshOnboardingToken, onboardingIdentity, sessionLifecycle, synchronizeOnboardingRequest]);

  const submitNewTenantRequest = useCallback(
    async (input: SubmitNewTenantAccessRequest): Promise<MyAccessRequestStatusResponse> => {
      if (onboardingIdentity === null) {
        throw new Error('Your onboarding sign-in has expired. Sign in again to continue.');
      }

      const generation = sessionLifecycle().capture();
      setError(null);
      setIsLoading(true);
      return await withCleanup(async () => {
        try {
          const token = await acquireFreshOnboardingToken(
            'Confirm your secure sign-in to submit your workspace request.',
            generation,
          );
          await apiClient.submitNewTenantRequest(token, input);
          return await synchronizeOnboardingRequest(token, onboardingIdentity, generation);
        } catch (error) {
          const message = toAuthErrorMessage(
            error,
            'The workspace request could not be submitted. Try again.',
          );

          if (sessionLifecycle().isCurrent(generation)) setError(message);
          throw new Error(message, { cause: error });
        }
      }, () => {
        setIsLoading(false);
      });
    },
    [acquireFreshOnboardingToken, onboardingIdentity, sessionLifecycle, synchronizeOnboardingRequest],
  );

  const submitJoinTenantRequest = useCallback(
    async (input: SubmitJoinAccessRequest): Promise<MyAccessRequestStatusResponse> => {
      if (onboardingIdentity === null) {
        throw new Error('Your onboarding sign-in has expired. Sign in again to continue.');
      }

      const generation = sessionLifecycle().capture();
      setError(null);
      setIsLoading(true);
      return await withCleanup(async () => {
        try {
          const token = await acquireFreshOnboardingToken(
            'Confirm your secure sign-in to submit your workspace join request.',
            generation,
          );
          await apiClient.submitJoinTenantRequest(token, input);
          return await synchronizeOnboardingRequest(token, onboardingIdentity, generation);
        } catch (error) {
          const message = toAuthErrorMessage(
            error,
            'The workspace join request could not be submitted. Try again.',
          );

          if (sessionLifecycle().isCurrent(generation)) setError(message);
          throw new Error(message, { cause: error });
        }
      }, () => {
        setIsLoading(false);
      });
    },
    [acquireFreshOnboardingToken, onboardingIdentity, sessionLifecycle, synchronizeOnboardingRequest],
  );

  const switchTenant = useCallback(async (tenantId: string): Promise<void> => {
    // Taken before approval, which can wait for uploads to be cancelled.
    const generation = sessionLifecycle().capture();
    if (!(await approveSessionChange('switch-workspace'))) throw new SessionChangeDeclinedError();
    setError(null);
    setIsLoading(true);
    await withCleanup(async () => {
      try {
        await runSerializedSessionOperation(async () => {
          if (!sessionLifecycle().isCurrent(generation)) return;
          await apiClient.switchTenant({ tenant_id: tenantId });
          const session = await apiClient.getAuthSession();
          if (session.authenticated) {
            await applyAuthenticatedSession(session, generation);
          } else {
            clearAuthenticatedSessionState();
          }
        });
      } catch (error) {
        const message = toAuthErrorMessage(
          error,
          'The workspace could not be switched. Try again.',
        );
        setError(message);
        throw new Error(message, { cause: error });
      }
    }, () => {
      setIsLoading(false);
    });
  }, [applyAuthenticatedSession, clearAuthenticatedSessionState, runSerializedSessionOperation, sessionLifecycle]);

  // No loading state: the page stays mounted, so anything typed into it survives the toggle.
  const setDemoMode = useCallback(async (enabled: boolean): Promise<void> => {
    const generation = sessionLifecycle().capture();
    setError(null);
    try {
      const adopted = await runSerializedSessionOperation(async () => {
        if (!sessionLifecycle().isCurrent(generation)) return false;
        return await applyAuthenticatedSession(
          enabled ? await apiClient.startDemoMode() : await apiClient.stopDemoMode(),
          generation,
        );
      });
      // Other open tabs still show what the old session could see until they reconcile.
      if (adopted) publishAuthRenewed();
    } catch (error) {
      const message = toAuthErrorMessage(
        error,
        enabled ? 'Demo mode could not be started. Try again.' : 'Demo mode could not be stopped. Try again.',
      );
      setError(message);
      throw new Error(message, { cause: error });
    }
  }, [applyAuthenticatedSession, runSerializedSessionOperation, sessionLifecycle]);

  const logout = useCallback(async (): Promise<void> => {
    if (!(await approveSessionChange('sign-out'))) return;
    // Signing out never waits for a renewal; one on its way finds its generation ended.
    endSessionGeneration();
    setError(null);
    setIsLoading(true);
    await withCleanup(async () => {
      try {
        await apiClient.signOut();
      } catch (error) {
        setError(
          toAuthErrorMessage(
            error,
            'The session could not be ended cleanly. You have been signed out locally.',
          ),
        );
      }
    }, () => {
      clearAuthenticatedSessionState();
      clearOnboardingState();
      setShowExpiredDialog(false);
      setSessionRenewalError(null);
      setSessionRenewalState('action_required');
      setIsLoading(false);
      publishAuthSignedOut();
    });
  }, [clearAuthenticatedSessionState, clearOnboardingState, endSessionGeneration]);

  const isSignedIn = user !== null;
  useEffect(() => {
    if (!isSignedIn || sessionDeadline === null || typeof window === 'undefined') {
      return undefined;
    }

    const delayMs = Math.max(sessionDeadline * 1000 - Date.now(), 0);
    const timeoutId = window.setTimeout(() => {
      if (userRef.current === null || renewalPromiseRef.current !== null) {
        return;
      }

      if (sessionHasRunOut(sessionDeadlineRef.current, Date.now())) {
        showSessionContinuation('action_required');
      }
    }, delayMs);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isSignedIn, sessionDeadline, showSessionContinuation]);

  const handleSignInAfterExpiry = useCallback(() => {
    // Guard: if already signing in, do not trigger another interaction.
    if (sessionRenewalState === 'signing_in') {
      return;
    }

    setSessionRenewalState('signing_in');
    setSessionRenewalError(null);

    // In dev mode, re-bootstrap inline using the current user's identity
    // instead of navigating to /login (which would lose page state). The
    // prompt opens here, inside the click, so the browser lets it through.
    const generation = sessionLifecycle().capture();
    const tokenPromise = isDevAuthMode()
      ? createDevIdentityProviderClient({
        email: userRef.current?.email ?? 'dev@local',
        name: userRef.current?.display_name ?? 'Dev User',
      }).signInInteractive()
      : signInInteractively();

    void runSerializedSessionOperation(async () => {
      const { token } = await tokenPromise;
      const bootstrap = await bootstrapRenewal(token, generation);
      if (bootstrap === SESSION_ENDED) return;
      if (bootstrap.status === 'registered') {
        await adoptRenewedSession(bootstrap, generation);
        return;
      }

      await applyBootstrapResult(bootstrap, token, generation);
      setError(null);
      setSessionRenewalState('action_required');
      setSessionRenewalError(null);
      setShowExpiredDialog(false);
    }).catch((error: unknown) => {
      // A sign-out since the click left nothing to continue.
      if (!sessionLifecycle().isCurrent(generation)) return;
      // When MSAL reports that an interaction is already in progress, stay in
      // "signing_in" state instead of flipping to "failed" — opening a second
      // popup would fail immediately and create the flash loop.
      if (
        error instanceof IdentityTokenAcquisitionError
        && error.kind === 'in_progress'
      ) {
        return;
      }

      publishAuthFailed();
      setSessionRenewalState('failed');
      setSessionRenewalError(
        error instanceof Error && error.message.trim().length > 0
          ? error.message
          : 'The secure sign-in did not complete. Try again.',
      );
      setShowExpiredDialog(true);
    });
  }, [adoptRenewedSession, applyBootstrapResult, bootstrapRenewal, runSerializedSessionOperation, sessionLifecycle, sessionRenewalState]);

  const acceptTerms = useCallback(async () => {
    const updatedUser = await apiClient.acceptTerms({ terms_version: CURRENT_TERMS_VERSION });
    setUser((currentUser) => {
      if (currentUser === null) {
        return currentUser;
      }

      return {
        ...currentUser,
        accepted_terms_at: updatedUser.accepted_terms_at ?? new Date().toISOString(),
      };
    });
  }, []);

  const hasPermission = useCallback(
    (permission: Permission): boolean =>
      (user?.is_superadmin ?? false) ||
      (user?.permissions.includes(permission) ?? false),
    [user],
  );

  const value = useMemo(
    () => ({
      availableMemberships,
      error,
      isAuthenticated: hasAuthenticatedSession,
      isLoading,
      onboardingIdentity,
      onboardingRequest,
      pendingInvitations,
      pendingPlatformInvitations,
      requiresOnboarding: onboardingIdentity !== null && user === null,
      termsAccepted: hasAcceptedCurrentTerms(user),
      user,
      hasPermission,
      login,
      logout,
      acceptTerms,
      refreshOnboardingRequest,
      retryOnboardingBootstrap,
      setDemoMode,
      submitJoinTenantRequest,
      submitNewTenantRequest,
      switchTenant,
    }),
    [
      acceptTerms,
      availableMemberships,
      error,
      hasAuthenticatedSession,
      hasPermission,
      isLoading,
      login,
      logout,
      onboardingIdentity,
      onboardingRequest,
      pendingInvitations,
      pendingPlatformInvitations,
      refreshOnboardingRequest,
      retryOnboardingBootstrap,
      setDemoMode,
      submitJoinTenantRequest,
      submitNewTenantRequest,
      switchTenant,
      user,
    ],
  );

  return (
    <AuthContext value={value}>
      {children}
      <SessionExpiredDialog
        error={sessionRenewalError}
        open={showExpiredDialog}
        state={sessionRenewalState}
        onSignIn={handleSignInAfterExpiry}
      />
    </AuthContext>
  );
}
